import React, { useState } from "react";
import { CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { saveGuestChat } from "@/components/ChatDrawer";
import { BDC_PHONE_DISPLAY, BDC_PHONE_HREF } from "@/config/brand";

export const RequestAppointmentModal = ({
  open,
  onOpenChange,
  vehicle,
  onLeadCreated,
  onContinueChat,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [form, setForm] = useState({
    name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdLead, setCreatedLead] = useState(null);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !vehicle) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", {
        vehicle_id: vehicle.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message || undefined,
      });
      setCreatedLead(data);
      // Persist guest token so the visitor can resume chat from the same browser.
      // For customer submissions we skip localStorage — /me/leads is the source of truth.
      if (!isAuthenticated && data?.guest_chat_token && data?.chat_thread_id) {
        saveGuestChat({
          threadId: data.chat_thread_id,
          token: data.guest_chat_token,
          email: form.email,
          vehicleId: vehicle.id,
        });
      }
      setSuccess(true);
      toast.success("Request sent — our BDC team will reach out shortly.");
      onLeadCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o) => {
    onOpenChange(o);
    if (!o) {
      setSuccess(false);
      setCreatedLead(null);
    }
  };

  const canChat = !!(createdLead?.chat_thread_id && (createdLead?.guest_chat_token || (isAuthenticated && user?.role === "customer")));

  const handleContinueChat = () => {
    if (!canChat) return;
    // Hand off the chat context to the parent (VehicleDetail owns the single
    // ChatDrawer instance). Then close the modal cleanly.
    onContinueChat?.({
      thread_id: createdLead.chat_thread_id,
      guest_token: createdLead.guest_chat_token || null,
      is_customer: !!(isAuthenticated && user?.role === "customer"),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="request-appt-modal">
        {success ? (
          <div className="py-6 text-center" data-testid="request-appt-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 size={30} />
            </div>
            <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
              You're on the list
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Our BDC concierge will reach out within one business hour to confirm your
              appointment for the {vehicle?.year} {vehicle?.make} {vehicle?.model}.
            </p>

            {canChat && (
              <div className="mt-6 rounded-xl border border-brand/20 bg-brand-light/30 p-4 text-left">
                <p className="text-sm font-semibold text-brand flex items-center gap-2">
                  <MessageCircle size={15} /> Prefer to keep the conversation going?
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  Start a live chat with our BDC team — we usually reply within business hours.
                </p>
                <Button
                  className="mt-3 w-full bg-brand hover:bg-brand-hover text-white"
                  onClick={handleContinueChat}
                  data-testid="request-appt-continue-chat-btn"
                >
                  <MessageCircle size={16} /> Continue in chat
                </Button>
              </div>
            )}

            <DialogClose asChild>
              <Button
                variant="outline"
                className="mt-4"
                data-testid="request-appt-close-btn"
              >
                Done
              </Button>
            </DialogClose>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                Request an appointment
              </DialogTitle>
              <DialogDescription>
                {vehicle
                  ? `We'll help you see the ${vehicle.year} ${vehicle.make} ${vehicle.model} at your convenience.`
                  : "Tell us how to reach you and we'll be in touch shortly."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-3">
              <div>
                <Label htmlFor="req-name" className="text-xs uppercase tracking-wider text-slate-500">
                  Full name
                </Label>
                <Input
                  id="req-name"
                  required
                  minLength={1}
                  value={form.name}
                  onChange={update("name")}
                  className="mt-1"
                  data-testid="request-appt-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="req-email" className="text-xs uppercase tracking-wider text-slate-500">
                    Email
                  </Label>
                  <Input
                    id="req-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="mt-1"
                    data-testid="request-appt-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="req-phone" className="text-xs uppercase tracking-wider text-slate-500">
                    Phone
                  </Label>
                  <Input
                    id="req-phone"
                    required
                    minLength={6}
                    value={form.phone}
                    onChange={update("phone")}
                    className="mt-1"
                    data-testid="request-appt-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="req-message" className="text-xs uppercase tracking-wider text-slate-500">
                  Anything we should know? (optional)
                </Label>
                <Textarea
                  id="req-message"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Best time to visit, trade-in details, financing questions…"
                  className="mt-1"
                  data-testid="request-appt-message-input"
                />
              </div>
            </div>

            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <a
                href={BDC_PHONE_HREF}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-brand font-medium"
                data-testid="request-appt-bdc-phone-link"
              >
                <Phone size={14} className="text-brand" />
                Prefer to call? <span className="font-semibold text-brand">{BDC_PHONE_DISPLAY}</span>
              </a>
              <div className="flex gap-2 justify-end">
                <DialogClose asChild>
                  <Button type="button" variant="outline" data-testid="request-appt-cancel-btn">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gold hover:bg-gold-hover text-white"
                  data-testid="request-appt-submit-btn"
                >
                  {submitting ? "Sending…" : "Send request"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
